/* eslint-disable no-case-declarations */
import { DateTime } from 'luxon';
import sqliteParser from 'sqlite-parser';
import { encodeSpecialChars, lengthWithDoubleNewlines } from '../stringUtil';
import { prependVar } from './ProcedureLogicUtil';

/**
 * Goes through a sqlite condition and finds all quoted sections. This will find both double and single quotes.
 * Then, replace each instance of quoted text with itself with the quote characters INSIDE removed.
 * Ex.  [var = "Parkinson's Disease" OR var = 'He said "Hello"'] -> [var = "Parkinsons Disease" OR var = 'He said Hello']
 * This is useful for not causing issues with the sqlite parser.
 * @param sqliteWhere - string containing condition
 * @returns stripped string as explained above.
 */
export const sanitizeCondition = (sqliteWhere) => {
  let newString = sqliteWhere;
  const instances = [...sqliteWhere.matchAll(/(["'“‘]).*?(?<!\\)(\\\\)*\1/g)];
  instances.forEach((i) => {
    const str = i[0].substring(1, i[0].length - 1);
    newString = newString.replace(str, encodeSpecialChars(str));
  });
  return newString;
};

/**
 * Parses a sqlite where clause to json as it appears in our crio database.
 * @returns json for the where clause
 * @param sqliteWhere
 */
const parseSqliteStatement = (sqliteWhere) => {
  const whereExtractor = (ast) => ast.statement[0].where[0];
  return whereExtractor(sqliteParser(`select 1
                                      from procedure
                                      where ${sanitizeCondition(sqliteWhere).replace(/"/g, '\'')}`));
};
export { parseSqliteStatement };

/**
 * Parses crio arrow syntax to extract the where clause and jsonify it.
 * @param {string} sqliteWhereWithArrow
 * @returns json for the where clause
 */
const parseSqliteStatementFromArrowSyntax = (sqliteWhereWithArrow) => {
  const whereClause = sqliteWhereWithArrow.split(' -> ')[0];
  return parseSqliteStatement(whereClause);
};
export { parseSqliteStatementFromArrowSyntax };

/*
Recreated functions that clients can use (derived from SQLite Functions originally)
*/
const functions = {
  length: (string) => (string ? lengthWithDoubleNewlines(string) : 0),
  // TODO .replace with regexp or see about replaceAll as an option?
  replace: (string, strToReplace, strToReplaceWith) => {
    if (!string) return string;
    const regexp = typeof strToReplace === 'string' ? new RegExp(strToReplace, 'g') : strToReplace;
    return string.replace(regexp, strToReplaceWith);
  },
  match: (string, regexp) => string && typeof string === 'string' && string.match(regexp) !== null,
  addTime: (dateTimeInput, mod) => {
    const dateTimeOutput = dateTimeInput;
    if (mod.match(/\d+\s\w/gi)) {
      const split = mod.split(' ');
      if (split[1].startsWith('day')) {
        return dateTimeOutput.plus({ days: parseInt(split[0], 10) });
      }
      if (split[1].startsWith('hour')) {
        return dateTimeOutput.plus({ hours: parseInt(split[0], 10) });
      }
      if (split[1].startsWith('minute')) {
        return dateTimeOutput.plus({ minutes: parseInt(split[0], 10) });
      }
      if (split[1].startsWith('second')) {
        return dateTimeOutput.plus({ seconds: parseInt(split[0], 10) });
      }
      if (split[1].startsWith('month')) {
        return dateTimeOutput.plus({ months: parseInt(split[0], 10) });
      }
      if (split[1].startsWith('year')) {
        return dateTimeOutput.plus({ years: parseInt(split[0], 10) });
      }
    }
    return dateTimeOutput;
  },
  datetime: (input, ...mods) => {
    if (!input) return null;
    const baseDateTime = typeof input === 'string'
      ? (input === 'now'
        // Resets DateTime to midnight of the day so current_date is equal to today's date in comparisons (ignores time)
        ? DateTime.now().startOf('day')
        : DateTime.fromJSDate(new Date(input.includes(':') ? input : `${input} 00:00`)))
      : input;
    return mods.reduce((acc, mod) => {
      if (mod === 'start of month') {
        return acc.startOf('month');
      }
      if (mod === 'start of year') {
        return acc.startOf('year');
      }
      if (mod === 'start of day') {
        return acc.startOf('day');
      }
      if (mod.match(/[weekday]\s\d+/gi)) {
        const dayNum = parseInt(mod.split(' ')[1], 10);
        // Sunday = 7 in luxon, but 0 in sqlite
        return acc.set({ weekday: dayNum === 0 ? 7 : dayNum }).startOf('day');
      }
      if (mod === 'utc') {
        return acc.toUTC();
      }
      if (mod === 'localtime') {
        return acc.toLocal();
      }
      // try to add time
      return functions.addTime(acc, mod);
    }, baseDateTime);
  },
  date: (input, ...mods) => functions.datetime(input, ...mods),
  time: (input, ...mods) => functions.datetime(input, ...mods),
  list: (string) => (string ? string.split('\n') : []),
  julianday: (input, ...mods) => {
    if (!input) return null;
    // 2440587.5 is the baseline number of juliandays
    // 86400000 is the conversion to days (24 * 60 * 60, etc.)
    return functions.datetime(input, ...mods) / 86400000 + 2440587.5;
  },
  false: () => false,
};
export { functions };

/**
 * Parses a sqlite json, assuming it's a literal type. Returns null if no recognized type is found
 * @param json
 * @param replaceWildcards
 * @returns {string|RegExp|number}
 */
const convertSqliteJsonLiteral = (json, replaceWildcards = false) => {
  if (json.variant === 'date' && json.value === 'current_date') {
    return 'functions.datetime(\'now\')';
  }
  if (json.variant === 'decimal') {
    return parseFloat(json.value);
  }
  if (json.variant === 'null') {
    return 'null';
  }

  if (replaceWildcards) {
    if (json.value.includes('%')) {
      return new RegExp(`^${json.value.replace(/%/g, '[\\s\\S]*')}$`, 'g');
    }
    return new RegExp(`^${json.value}$`, 'g');
  }

  if (json.variant === 'text') {
    // do NOT remove this escaping - it will break the sqlite parser
    // eslint-disable-next-line
    return `\"${json.value}\"`;
  }
  return 'null';
};

/**
 * Converts json ast from sqlite-parser library back to a conditional,
 * with modifications for use in js function
 * @param {json} json
 * @param replaceWildcards whether to replace %
 * @returns a string where clause to be executed for a js function
 */
const convertSqliteJsonForNodeRules = (json, replaceWildcards = false) => {
  if (json.type === 'expression') {
    if (json.variant === 'list') {
      return json.expression.map((listItem) => convertSqliteJsonForNodeRules(listItem, replaceWildcards));
    }
    if (json.variant === 'case') {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return convertSqliteJsonCase(json);
    }
    if (json.variant === 'operation') {
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      return convertSqliteJsonOperation(json, replaceWildcards);
    }
  } else if (json.type === 'function') {
    return `functions.${convertSqliteJsonForNodeRules(json.name, replaceWildcards)}(${convertSqliteJsonForNodeRules(json.args, replaceWildcards)})`;
  } else if (json.type === 'identifier') {
    if (json.variant === 'column') {
      return `fact.${prependVar(json.name.toLowerCase())}`;
    }
    if (json.variant === 'function' && json.name === 'list') {
      return json.name.toLowerCase();
    }
    if (json.variant === 'function' && functions.hasOwnProperty(json.name.toLowerCase())) {
      return json.name.toLowerCase();
    }
  } else if (json.type === 'literal') {
    return convertSqliteJsonLiteral(json, replaceWildcards);
  }
  return 'false';
};
export { convertSqliteJsonForNodeRules };

/**
 * Parse a sqlite json, assuming it's a case type.
 * @param json
 */
const convertSqliteJsonCase = (json) => {
  const { discriminant, expression } = json;
  const elseCase = expression.find((condition) => condition.variant === 'else') || { consequent: parseSqliteStatement('null') };
  const baseCase = convertSqliteJsonForNodeRules(elseCase.consequent);
  const cases = expression.reduce((casesThusFar, condition) => {
    if (condition.variant === 'when') {
      return discriminant
        ? `(${convertSqliteJsonForNodeRules(discriminant)} === ${convertSqliteJsonForNodeRules(condition.condition)}) ? ${convertSqliteJsonForNodeRules(condition.consequent)} : ${casesThusFar}`
        : `(${convertSqliteJsonForNodeRules(condition.condition)}) ? ${convertSqliteJsonForNodeRules(condition.consequent)} : ${casesThusFar}`;
    }
    return casesThusFar;
  }, baseCase);
  return `(${cases})`;
};

/**
 * Parse a sqlite json, assuming it's an operation type. Returns null if no suitable operation is found
 * @param json
 * @returns {string}
 */
const convertSqliteJsonOperation = (json, replaceWildcards = false) => {
  const left = json.left ? convertSqliteJsonForNodeRules(json.left, replaceWildcards) : null;
  const right = json.right ? convertSqliteJsonForNodeRules(json.right, replaceWildcards) : null;

  if (json.operator === 'not') {
    const expr = convertSqliteJsonForNodeRules(json.expression);
    return `TVL.not(${expr})`;
  }

  if (json.operator === '-') {
    const expr = convertSqliteJsonForNodeRules(json.expression);
    return `TVL.sub(0, ${expr})`;
  }

  switch (json.operation) {
    case 'and':
      return `TVL.and(${left}, ${right})`;
    case 'or':
      return `TVL.or(${left}, ${right})`;
    case 'between':
    case 'not between':
      const betweenLeft = convertSqliteJsonForNodeRules(json.right.left);
      const betweenRight = convertSqliteJsonForNodeRules(json.right.right);
      const coreExpressionBetween = `TVL.and(TVL.gte(${left}, ${betweenLeft}), TVL.lte(${left}, ${betweenRight}))`;

      return json.operation === 'not between'
        ? `TVL.not(${coreExpressionBetween})`
        : coreExpressionBetween;
    case 'in':
    case 'not in':
      const coreExpressionIn = `TVL.in(${left}, [${right}].flat())`;
      return json.operation === 'not in'
        ? `TVL.not(${coreExpressionIn})`
        : coreExpressionIn;
    case '=':
      return `TVL.eq(${left}, ${right})`;
    case 'is':
      return `TVL.is(${left}, ${right})`;
    case 'is not':
      return `TVL.isNot(${left}, ${right})`;
    case '<>':
    case '!=':
      return `TVL.neq(${left}, ${right})`;
    case 'like':
    case 'not like':
      // The pattern to match
      const leftPattern = convertSqliteJsonForNodeRules(json.left, true);
      // The value to match
      const rightValue = convertSqliteJsonForNodeRules(json.right, true);
      const coreExpressionLike = `functions.match(${leftPattern}, ${rightValue})`;

      return json.operation === 'not like'
        ? `TVL.not(${coreExpressionLike})`
        : coreExpressionLike;
    case '<':
      return `TVL.lt(${left}, ${right})`;
    case '<=':
      return `TVL.lte(${left}, ${right})`;
    case '>':
      return `TVL.gt(${left}, ${right})`;
    case '>=':
      return `TVL.gte(${left}, ${right})`;
    case '-':
      return `TVL.sub(${left}, ${right})`;
    case '+':
      return `TVL.add(${left}, ${right})`;
    case '*':
      return `TVL.mul(${left}, ${right})`;
    case '/':
      return `TVL.div(${left}, ${right})`;
    case '%':
      return `TVL.mod(${left}, ${right})`;
    default:
      return 'null';
  }
};

/**
 * Utility method that converts a sqlite where to json, then json to javascript conditional
 * @param {string} sqliteCondition
 * @returns a stringified javascript conditional for the given sqlite condition
 */
const convertSqliteForNodeRules = (sqliteCondition) => convertSqliteJsonForNodeRules(
  sqliteCondition.includes('->')
    ? parseSqliteStatementFromArrowSyntax(sqliteCondition)
    : parseSqliteStatement(sqliteCondition),
);
export { convertSqliteForNodeRules };

/**
 * Extracts the variable names from a json expression that adheres to the sqlite parsed json.
 * @param node
 * @param acc
 * @returns {*[]|*}
 */
const extractVariableNamesFromConditionHelper = (node, acc) => {
  // we are looking for these identifier leaf-nodes.
  if (node.type === 'identifier' && node.variant === 'column') {
    acc.push(prependVar(node.name));
    return acc;
  }
  // everything after this comment is about traversing the AST to find identifiers

  if (node.left && node.right) {
    return extractVariableNamesFromConditionHelper(node.left, acc).concat(extractVariableNamesFromConditionHelper(node.right, acc));
  }

  if (node.type === 'function') {
    return node.args.expression.reduce((reduced, paramNode) => extractVariableNamesFromConditionHelper(paramNode, reduced), acc);
  }

  if (node.type === 'expression' && node.variant === 'case') {
    // e.g. case discriminant when 'Foo' then 1 else 0 end
    if (node.discriminant) {
      return extractVariableNamesFromConditionHelper(node.discriminant, acc);
    }

    // e.g. case when var = 'Foo' then 1 else 0 end
    return node.expression.reduce((reduced, paramNode) => extractVariableNamesFromConditionHelper(paramNode, reduced), acc);
  }

  if (node.type === 'expression' && node.variant === 'operation') {
    return extractVariableNamesFromConditionHelper(node.expression, acc);
  }
  // case when variable name occurs in a list. This is how we handle multiselect selection alerts.
  if (node.type === 'expression' && node.variant === 'list' && node.expression[0].args) {
    return extractVariableNamesFromConditionHelper(node.expression[0].args.expression[0], acc);
  }

  if (node.type === 'condition') {
    if (node.variant === 'when') {
      return extractVariableNamesFromConditionHelper(node.condition, acc).concat(extractVariableNamesFromConditionHelper(node.consequent, acc));
    }
    if (node.variant === 'else') {
      return extractVariableNamesFromConditionHelper(node.consequent, acc);
    }
  }
  return acc;
};
const extractVariableNamesFromCondition = (node) => [...new Set(extractVariableNamesFromConditionHelper(node, []))];
export { extractVariableNamesFromCondition };
